import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useTrackPageView } from "@/lib/useTrackPageView";
import {
  Airplane, ArrowLeft, CheckCircle, XCircle, Warning, Info,
  Trophy, Calendar, CurrencyDollar, Shield, TelegramLogo, MapPin,
} from "@phosphor-icons/react";

const SEA_COUNTRIES = ["Malaysia", "Singapore", "Thailand", "Vietnam", "Philippines", "Indonesia", "Brunei", "Cambodia", "Laos", "Myanmar"];

const Section = ({ id, num, title, icon: Icon, children }) => (
  <section id={id} className="mb-8 scroll-mt-24">
    <div className="flex items-start gap-3 mb-3">
      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-orange-500/10 border border-orange-500/30 flex items-center justify-center">
        <Icon size={18} weight="duotone" className="text-orange-400" />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-widest text-gray-500 font-mono">Section {num}</p>
        <h2 className="text-lg md:text-xl font-bold text-white">{title}</h2>
      </div>
    </div>
    <div className="pl-12 text-sm text-gray-300 space-y-3 leading-relaxed">{children}</div>
  </section>
);

const Rule = ({ ok = true, children }) => (
  <div className="flex items-start gap-2">
    {ok ? (
      <CheckCircle size={16} weight="fill" className="text-emerald-400 mt-0.5 flex-shrink-0" />
    ) : (
      <XCircle size={16} weight="fill" className="text-red-400 mt-0.5 flex-shrink-0" />
    )}
    <p className="flex-1">{children}</p>
  </div>
);

export default function TermsAndConditions({ embedded = false }) {
  useTrackPageView(embedded ? null : "terms");
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/campaign-settings/public"); setSettings(data); } catch { /* noop */ }
    })();
  }, []);

  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" }) : "—";
  const startDate = fmt(settings?.campaign_start);
  const endDate = fmt(settings?.campaign_end);

  const content = (
    <div className={embedded ? "" : "max-w-3xl mx-auto px-4 py-12"}>
      {!embedded && (
        <>
          <Link to="/public-leaderboard" className="inline-flex items-center gap-2 text-xs text-gray-400 hover:text-white mb-6">
            <ArrowLeft size={14} /> Back to leaderboard
          </Link>
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/30 mb-3">
              <Airplane size={14} className="text-orange-400" />
              <span className="text-xs uppercase tracking-widest font-mono text-orange-300">Official Rules</span>
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-2">Terms &amp; Conditions</h1>
            <p className="text-sm text-gray-400">MBE Road To Bali Challenge · Please read carefully before participating.</p>
            {settings && (
              <p className="text-xs font-mono text-gray-500 mt-2">
                Campaign window: {startDate} → {endDate}
              </p>
            )}
          </div>
        </>
      )}

      <Section id="overview" num="01" title="Campaign Overview" icon={Trophy}>
        <p>
          The <strong className="text-white">MBE Road To Bali Challenge</strong> is a lot-volume trading incentive
          program run by MBE International for clients trading through our XM Partners IB channel. Eligible traders
          who reach set milestones during the campaign window will receive cash rewards and a chance to win a fully
          arranged Bali trip.
        </p>
      </Section>

      <Section id="eligibility" num="02" title="Eligibility" icon={CheckCircle}>
        <Rule>Open to any individual client with a valid XM trading account (MT4 or MT5) opened under MBE&apos;s IB code.</Rule>
        <Rule><strong className="text-white">Referral code: <span className="font-mono text-orange-300">MBE26</span></strong> must be used when opening the trading account. Accounts opened without this code (or under a different IB) do NOT qualify.</Rule>
        <Rule>Existing XM clients who open a NEW trading account with our referral code are eligible for that new account.</Rule>
        <Rule><strong className="text-white">No minimum deposit</strong> is required to participate.</Rule>
        <Rule ok={false}>MBE staff, immediate family, and affiliated introducing brokers are not eligible.</Rule>
      </Section>

      <Section id="auto-acceptance" num="03" title="Auto-Acceptance of Terms" icon={Info}>
        <div className="p-4 rounded-lg bg-orange-500/5 border border-orange-500/30 mb-3">
          <p className="text-sm text-orange-100">
            <strong className="text-white">Important:</strong> By opening (or having previously opened) an XM
            trading account under referral code <span className="font-mono text-orange-300">MBE26</span> and
            participating in this campaign, you are deemed to have{" "}
            <strong className="text-white">automatically read, understood and accepted</strong> these Terms
            &amp; Conditions in full — <strong>without any further review, signature, or explicit tick-box
            required</strong>.
          </p>
        </div>
        <Rule>Participants who register through the XM broker integration are automatically enrolled once their account is detected; no separate acceptance step is required.</Rule>
        <Rule>Participants who register manually via the public registration form must additionally tick the acceptance box; a timestamped audit record is kept.</Rule>
        <Rule>Continued trading activity during the campaign period is treated as ongoing affirmation of these Terms as amended from time to time.</Rule>
        <p className="text-xs text-gray-500 italic">
          If you do NOT agree to these Terms, you must not participate. Refrain from placing SILVER (XAGUSD)
          trades under referral code <span className="font-mono">MBE26</span> during the campaign period, and
          contact MBE support to opt out.
        </p>
      </Section>

      <Section id="period" num="04" title="Campaign Period" icon={Calendar}>
        <p>
          The campaign runs from <strong className="text-white">{startDate}</strong> to
          <strong className="text-white"> {endDate}</strong> (UTC). Only trades <strong>closed</strong> within
          this window count toward milestones. Trades closed before the start or after the end date are recorded
          for analytics but do not count.
        </p>
      </Section>

      <Section id="qualifying-trades" num="05" title="Qualifying Trades" icon={CurrencyDollar}>
        <Rule>Only trades on <strong className="text-white">SILVER (XAGUSD)</strong> count toward campaign milestones.</Rule>
        <Rule>Trades must be <strong className="text-white">closed</strong> — open, pending, or partially filled positions do not qualify.</Rule>
        <Rule>Trade <strong>close time</strong> must fall inside the campaign window.</Rule>
        <Rule><strong className="text-white">Hedged trades are counted</strong> in full — no reduction for opposing positions.</Rule>
        <Rule>Trades on other symbols (Forex, Gold, Crypto, etc.) are tracked for behaviour analytics but do NOT contribute to milestone lot totals.</Rule>
      </Section>

      <Section id="milestones" num="06" title="Milestones &amp; Rewards" icon={Trophy}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 not-prose">
          <div className="p-4 rounded-lg border border-blue-500/30 bg-blue-500/5">
            <p className="text-xs uppercase text-gray-500 tracking-widest">Milestone 1</p>
            <p className="text-3xl font-bold text-white font-mono">10 lots</p>
            <p className="text-emerald-400 text-sm mt-1">USD $100 cash</p>
          </div>
          <div className="p-4 rounded-lg border border-blue-500/30 bg-blue-500/5">
            <p className="text-xs uppercase text-gray-500 tracking-widest">Milestone 2</p>
            <p className="text-3xl font-bold text-white font-mono">20 lots</p>
            <p className="text-emerald-400 text-sm mt-1">USD $200 cash</p>
          </div>
          <div className="p-4 rounded-lg border border-orange-500/40 bg-orange-500/10">
            <p className="text-xs uppercase text-gray-500 tracking-widest">Milestone 3</p>
            <p className="text-3xl font-bold text-white font-mono">30 lots</p>
            <p className="text-orange-300 text-sm mt-1">Bali Trip / Cash equivalent</p>
          </div>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          All rewards are cumulative — hitting 30 lots earns you the $100, $200, AND the Bali qualification.
        </p>
      </Section>

      <Section id="aggregation" num="07" title="Multi-Account Aggregation" icon={Info}>
        <p>
          If you hold <strong className="text-white">multiple trading accounts</strong> under the same XM client
          profile, all your Silver lots are <strong>aggregated across accounts</strong> for milestone calculation.
        </p>
        <p className="text-xs text-gray-500">
          Example: 12 lots on Account A + 18 lots on Account B = 30 lots total → Bali qualification.
          Both accounts must be opened with referral code <span className="font-mono text-orange-300">MBE26</span>.
        </p>
      </Section>

      <Section id="bali-trip" num="08" title="Bali Trip Details" icon={Airplane}>
        <Rule><strong className="text-white">SEA participants</strong> ({SEA_COUNTRIES.join(", ")}) receive an all-arranged Bali trip: <strong>flight + hotel accommodation</strong> included.</Rule>
        <Rule><strong className="text-white">Non-SEA participants</strong> receive an equivalent <strong>CASH prize</strong> in place of the physical trip.</Rule>
        <Rule><strong>1 PAX only</strong> per qualifying participant. No plus-ones. Additional guests are at participant&apos;s own cost.</Rule>
        <Rule>Cash alternative (if participant is unable / prefers not to travel): <strong className="text-white">70% of the Bali budget allocation</strong> paid in cash. The remaining 30% is retained by MBE as an administrative deduction.</Rule>
        <Rule>Trip date and itinerary will be announced <strong className="text-white">14 days after the campaign ends</strong> ({fmt(settings?.campaign_end ? new Date(new Date(settings.campaign_end).getTime() + 14 * 86400000).toISOString() : null)}).</Rule>
        <Rule>Bali trip fulfilment is subject to visa eligibility, passport validity (min 6 months), and MBE&apos;s selected travel window.</Rule>
        <Rule ok={false}>The trip is <strong>non-transferable to third parties</strong> (i.e., cannot be sold or gifted to someone else). Only the qualifying participant may claim.</Rule>
      </Section>

      <Section id="payouts" num="09" title="Payout Timeline" icon={CurrencyDollar}>
        <Rule>Milestone 1 &amp; 2 cash rewards ($100 / $200) will be paid within <strong className="text-white">7 to 14 business days</strong> after MBE&apos;s Ops team verifies the milestone.</Rule>
        <Rule>Payment method: credited to the participant&apos;s XM trading account or as separately agreed with MBE.</Rule>
        <Rule>Bali trip / cash equivalent will be arranged after campaign end and audit is complete.</Rule>
        <Rule ok={false}>MBE is not responsible for delays caused by third-party payment processors, banks, or the broker.</Rule>
      </Section>

      <Section id="fair-use" num="10" title="Fair Use &amp; Prohibited Activities" icon={Shield}>
        <p>
          MBE reserves the right to <strong className="text-white">audit, investigate, and disqualify</strong> any
          participant whose trading behaviour indicates gaming, abuse, or bad-faith activity. This includes but is
          not limited to:
        </p>
        <Rule ok={false}><strong>Wash trading</strong> — round-trip trades (opening and closing opposing positions in short succession) purely to inflate lot counts.</Rule>
        <Rule ok={false}><strong>Cross-trading</strong> — holding <strong className="text-white">opposing positions on the same asset across multiple accounts</strong> to abuse negative-balance protection, hedge risk-free, or inflate lot counts. Strictly banned regardless of whether accounts belong to the same participant or coordinated participants.</Rule>
        <Rule ok={false}><strong>Multi-account collusion</strong> — coordinated trading across separate participants&apos; accounts to game milestones.</Rule>
        <Rule ok={false}><strong>Suspicious deposit/withdrawal patterns</strong> — using deposits solely to hit lots then immediately withdrawing.</Rule>
        <Rule ok={false}><strong>Trading with bonus / promotional equity</strong> — lots opened using <strong className="text-white">broker-issued bonus credit</strong> (deposit bonuses, promo credits, or any non-cash equity) are <strong className="text-white">NOT counted</strong> as valid lots for this campaign. Participants may still enjoy any bonuses provided by the broker, but positions funded by that bonus equity will be excluded from milestone calculations.</Rule>
        <Rule ok={false}><strong>Broker rule violations</strong> — any trading behaviour that violates XM&apos;s terms of service.</Rule>
        <p className="text-xs text-gray-500 italic">
          Disqualified participants forfeit all campaign rewards, including any previously earned milestones that are still pending payment.
        </p>
      </Section>

      <Section id="force-majeure" num="11" title="Force Majeure &amp; Amendments" icon={Warning}>
        <Rule>MBE reserves the right to <strong>modify, suspend, or cancel</strong> the campaign at any time due to circumstances outside its reasonable control (regulatory changes, broker actions, natural disasters, pandemics, etc.).</Rule>
        <Rule>Terms may be updated during the campaign. The latest version is always available at <span className="font-mono text-blue-300">bali.mbealert.com/terms</span></Rule>
        <Rule>Material changes will be communicated via our Telegram support channel and email.</Rule>
      </Section>

      <Section id="contact" num="12" title="Support &amp; Contact" icon={TelegramLogo}>
        <p>
          For questions, disputes, or clarifications, contact MBE support via Telegram:
        </p>
        <a
          href="https://t.me/m/pKy1ugEjZmY1"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="terms-telegram-link"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-500 hover:bg-blue-400 text-white text-sm font-medium transition-colors mt-2"
        >
          <TelegramLogo size={16} weight="fill" />
          Contact MBE Support on Telegram
        </a>
      </Section>

      <div className="mt-10 pt-6 border-t border-white/10 text-xs text-gray-500">
        <p>
          By participating in the MBE Road To Bali Challenge, you acknowledge that you have read, understood, and
          agree to be bound by these Terms &amp; Conditions.
        </p>
        <p className="mt-2 font-mono">
          Version 1.2 · Effective from {startDate} · MBE International
        </p>
      </div>
    </div>
  );

  if (embedded) return content;

  return (
    <div className="min-h-screen bg-[#0B0F19] relative">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(37,99,235,0.1),transparent_50%),radial-gradient(circle_at_80%_80%,rgba(249,115,22,0.1),transparent_50%)] pointer-events-none" />
      <div className="relative z-10">{content}</div>
    </div>
  );
}
